import React, { useState, useCallback, useEffect } from 'react';
import { IRangeSliderProps, RangeSlider } from 'components/inputs/RangeSlider';
import './DesignerRangeSlider.less';
import classNames from 'classnames';

export interface IDesignerRangeSliderProps extends Omit<IRangeSliderProps, 'onChange'> {
  onValueChanged?: (value: number) => void;
  showZoomButtons?: boolean;
}

export function DesignerRangeSlider(props: IDesignerRangeSliderProps) {
  const { max, min, onValueChanged, showZoomButtons } = props;
  const [value, setValue] = useState<number>(props.value ?? min);
  const onRangeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(+e.target.value);
      onValueChanged && onValueChanged(+e.target.value);
    },
    [onValueChanged]
  );

  useEffect(() => {
    setValue(Math.max(props.value ?? 0, min));
  }, [min, props]);

  const getProgressStyle = useCallback((): React.CSSProperties => {
    return {
      background: `linear-gradient(to right, white 0%, white ${getSliderwidth(
        min,
        max,
        value
      )}%, #C2C2C2 ${getSliderwidth(min, max, value)}%, #C2C2C2 100%)`,
    };
  }, [max, min, value]);

  const incrementation = useCallback(() => {
    const newValue = value + (props.step ?? 1);

    if (newValue >= max) {
      return;
    }

    setValue(newValue);
    onValueChanged && onValueChanged(newValue);
  }, [max, props.step, value, onValueChanged]);

  const decrementation = useCallback(() => {
    const newValue = value - (props.step ?? 1);

    if (newValue <= min) {
      return;
    }

    setValue(newValue);
    onValueChanged && onValueChanged(newValue);
  }, [min, props.step, value, onValueChanged]);

  return (
    <li className="dp-designer-range-slider">
      {showZoomButtons && (
        <button
          onClick={decrementation}
          className="dp-designer-range-slider__button dp-designer-range-slider__button--minus"
        />
      )}
      <RangeSlider
        {...props}
        style={getProgressStyle()}
        className={classNames('dp-designer-range-slider__input', props.className)}
        onChange={onRangeChange}
        value={value}
      />
      {showZoomButtons && (
        <button
          onClick={incrementation}
          className="dp-designer-range-slider__button dp-designer-range-slider__button--plus"
        />
      )}
    </li>
  );
}

function getSliderwidth(min: number, max: number, value: number) {
  if (min > max) {
    return 0;
  }

  // change range to from 0 to max
  if (min < 0) {
    max -= min;
    value -= min;
  } else if (min > 0) {
    max -= min;
    value -= min;
  }

  // get percent
  return Math.trunc((value / max) * 100);
}
